import React, { ReactNode, useContext } from 'react';
import { PageMetricsProvider, MetricsTracker } from '@sm/metrics';

import { ExperimentProvider } from '../Experiments';
import BasePageHead from './BasePageHead';
import { StaticContext } from '../..';
import BasePageBody, { BasePageBodyProps } from './BasePageBody';
import { useGTM, useInitialRender } from './hooks';

export type BasePageProps = BasePageBodyProps & {
  legacyWeb: string;
  pageId: string;
  includeGTM?: boolean;
  includeExperiments?: boolean;
  pageMetricsAttributes?: object;
  children: ReactNode;
};

const BasePage = ({
  children,
  pageId,
  pageMetricsAttributes,
  legacyWeb,
  includeGTM = true,
  includeExperiments = true,
  ...bodyProps
}: BasePageProps) => {
  const isInitialRender = useInitialRender();
  const {
    user,
    environment,
    'client-config': clientConfig,
    GDPR,
  } = useContext(StaticContext);
  const { countryCode: country } = environment;
  const { gtmId, loggingAPIPath, dataAnalyticsAPIPath, amplitudeToken } =
    clientConfig;

  // init global metrics
  if (isInitialRender) {
    MetricsTracker.initialize({
      user,
      dataAnalyticsAPIPath,
      loggingAPIPath,
      country,
      legacyWeb,
      pageId,
      gtmId,
      amplitudeToken,
    });
  }
  useGTM(includeGTM);

  const BasePageInternals = (
    <PageMetricsProvider
      pageId={pageId}
      attributes={pageMetricsAttributes}
      GDPR={GDPR}
      user={user}
      country={country}
    >
      <BasePageHead />
      <BasePageBody {...bodyProps}>{children}</BasePageBody>
    </PageMetricsProvider>
  );

  if (includeExperiments) {
    return <ExperimentProvider>{BasePageInternals}</ExperimentProvider>;
  }

  return BasePageInternals;
};

export default BasePage;
